import {
  ReactNode,
  useMemo,
  useState,
  FocusEvent,
  useEffect,
  KeyboardEvent,
  useRef,
} from 'react';
import styles from './ListItemMenu.module.scss';
import { Button, IconButton } from '../pressables';
import { MenuContext, MenuOption } from './MenuContext';
import { MenuActionsContext } from './MenuActionsContext';

interface Props {
  children: ReactNode | ((args: { open: () => void }) => ReactNode);
  label?: string;
  icon?: ReactNode;
  options?: ReactNode;
  __autoclose?: boolean;
}

export function ListItemMenu({ __autoclose = true, ...props }: Props) {
  const [expanded, setExpanded] = useState(false);
  const menuOptionsMap = useMemo(() => new Map<string, MenuOption>(), []);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Runs on every render while open so it re-evaluates after an option's
  // disabled state changes (e.g. reordering items). Option effects run before
  // this one, so the map is already up to date. The currently focused option
  // keeps focus unless it became disabled, in which case focus moves to the
  // first non-disabled option.
  useEffect(() => {
    if (!expanded) {
      return;
    }
    const menuOptions = [...menuOptionsMap.values()];
    const active = menuOptions.find(
      (option) => option.ref.current === document.activeElement,
    );
    if (active && !active.disabled) {
      return;
    }
    const firstEnabled = menuOptions.find((option) => !option.disabled);
    firstEnabled?.ref.current?.focus();
  });

  const close = () => {
    if (__autoclose) {
      setExpanded(false);
    }
  };

  // Close when clicking outside the menu. The focus/blur handling alone is not
  // reliable: an option can lose focusability (e.g. it becomes disabled after
  // the click) which strands focus and prevents the blur from firing.
  useEffect(() => {
    if (!expanded) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded]);

  const onClick = () => {
    setExpanded((value) => !value);
  };

  const onBlur = (event: FocusEvent<HTMLUListElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      close();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const menuOptions = Array.from(menuOptionsMap.values());
    const focusedIndex = menuOptions.findIndex((e) => e.focused);

    switch (event.key) {
      case 'Escape':
        close();
        buttonRef.current?.focus();
        break;

      case 'Tab':
        close();
        break;

      case 'ArrowDown':
      case 'ArrowRight':
        (
          menuOptions.at(focusedIndex + 1) ?? menuOptions.at(0)
        )?.ref.current?.focus();
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        menuOptions.at(focusedIndex - 1)?.ref.current?.focus();
        break;

      default:
        break;
    }
  };

  const className = [styles['menu'], expanded && styles['menu-expanded']]
    .filter(Boolean)
    .join(' ');

  const open = () => {
    setExpanded(true);
  };

  return (
    <div ref={containerRef} className={styles['list-item-menu']}>
      {props.options ? (
        typeof props.children === 'function' ? (
          props.children({ open })
        ) : (
          props.children
        )
      ) : props.icon && !props.label ? (
        <IconButton
          aria-label="menu"
          onClick={onClick}
          variant="clear"
          size="xsmall"
        >
          {props.icon}
        </IconButton>
      ) : (
        <Button ref={buttonRef} onClick={onClick} variant="clear">
          {props.label ?? 'Menu'}
        </Button>
      )}

      <MenuContext.Provider value={menuOptionsMap}>
        <MenuActionsContext value={{ open, close }}>
          <ul
            className={className}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            role="menu"
          >
            {props.options ??
              (typeof props.children === 'function'
                ? props.children({ open })
                : props.children)}
          </ul>
        </MenuActionsContext>
      </MenuContext.Provider>
    </div>
  );
}
